
import { useMemo } from "react";
import Prism from "prismjs";
import "prismjs/components/prism-json";
import { CodeCopyButton } from "./CodeCopyButton";
import { toSafeCodeHtml } from "./code-display-utils";

interface JsonDisplayProps {
  json: string;
  maxHeight?: string;
  showCopyButton?: boolean;
  className?: string;
}

export function JsonDisplay({
  json,
  maxHeight = "300px",
  showCopyButton = true,
  className = "",
}: JsonDisplayProps) {
  // Format JSON - memoized to avoid re-formatting on every render
  const formattedJson = useMemo(() => {
    if (!json?.trim()) return "";
    try {
      // Try to parse and pretty-print JSON
      const parsed = JSON.parse(json);
      return JSON.stringify(parsed, null, 2);
    } catch {
      // If not valid JSON, return original
      return json;
    }
  }, [json]);

  // Apply Prism highlighting - generate HTML directly
  const highlightedHtml = useMemo(() => {
    if (!formattedJson) return "";
    try {
      let html = formattedJson;
      if (Prism?.languages?.json) {
        html = Prism.highlight(formattedJson, Prism.languages.json, "json");
      } else {
        return toSafeCodeHtml(formattedJson);
      }
      // Convert newlines to <br> for proper rendering
      return html.replace(/\n/g, "<br/>");
    } catch {
      return toSafeCodeHtml(formattedJson);
    }
  }, [formattedJson]);

  if (!json?.trim()) {
    return (
      <div className="text-sm text-gray-400 italic p-3">Không có dữ liệu</div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Copy button */}
      {showCopyButton && <CodeCopyButton value={formattedJson} />}

      {/* JSON code block */}
      <div
        className="overflow-auto rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
        style={{ maxHeight }}
      >
        <pre className="p-3! sm:p-4! pr-10! sm:pr-16! m-0! text-xs sm:text-sm leading-relaxed font-mono whitespace-pre-wrap! wrap-break-word">
          <code
            className="language-json block whitespace-pre-wrap!"
            dangerouslySetInnerHTML={{ __html: highlightedHtml }}
          />
        </pre>
      </div>
    </div>
  );
}
